<template>
  <math-field 
    :ref="(el) => {refMathField = el}" 
    class="virtual_keyboard"
    math-mode-space="\:"
    v-on:input="setInputValue"
    :readonly="prevIsCorrectAnswer ? true : false"
    :data-prev-is-correctAnswer="prevIsCorrectAnswer"
    @keyup.enter="emit('press-enter')"
    @focus="emit('focus', true)"
    @blur="emit('blur', null)"
  ></math-field>
</template>

<script setup lang="ts">
  import {ref, onMounted, onUnmounted} from 'vue';
  import { markdown as md } from "@/util/markdown";
  import { useWorkStore } from '@/store/student/work_store';
  // NFR-4079 from mathlive-0.95.5 virtual keyboard is share for all math input field and accessible using window.mathVirtualKeyboard
  declare global {
      interface Window { mathVirtualKeyboard: any; }
  }
  const props = withDefaults(
    defineProps<{
      isScience?: boolean;
      placeHolder?: string;
      suffix?: string;
      prevAnswerText?: string;
      prevIsCorrectAnswer?: boolean;
    }>(),
    {
      isScience: false,
      placeHolder: "",
      suffix: "",
      prevAnswerText: "",
      prevIsCorrectAnswer: false,
    }
  );
  
  const emit = defineEmits<{
    (e: 'press-enter'): void,
    (e: 'focus', id: boolean): void,
    (e: 'blur', id: null): void,
    (e: 'set-latex', id: string): void,
  }>()

  const workStore = useWorkStore()
  const restoreText = ref("")
  const refMathField = ref(null)
  const MANAVIRIA_MATH_KEYBOARD_LAYER = ref([
    {
      label: "数式",
      rows: [
        [
          { latex: "a" },
          { latex: "x" },
          { class: "separator w5" },
          { label: "7", key: "7" },
          { label: "8", key: "8" },
          { label: "9", key: "9" },
          { latex: "\\div" },
          { class: "separator w5" },
          { class:"tex small", latex: "\\dfrac{#1}{#0}", insert: "$$\\dfrac{#1}{#0}$$" },
          {
            class: "tex ",
            latex: "{#0}^2",
            insert: "$$#@^{2}$$",
          },
          {
            class: "tex ",
            latex: "{#0}^{#0}",
            insert: "$$#@^{#0}$$",
          },
          {
            class: "tex ",
            latex: "{#0}´",
            insert: "$$#@´$$",
          },
        ],
        [
          { latex: "b" },
          { latex: "y" },
          { class: "separator w5" },
          { label: "4", key: "4" },
          { label: "5", key: "5" },
          { label: "6", key: "6" },
          { latex: "\\times" },
          { class: "separator w5" },
          { latex: "\\pm" },
          {
            class: "tex",
            latex: "\\left({#0}\\right)",
            insert: "$$(#0)$$",
          },
          {
            class: "tex",
            latex: "\\sqrt{#0}",
            insert: "$$\\sqrt{#0}$$",
          },
          {
            class: "tex",
            latex: ">",
            variants: [">", "<", "\\geqq", "\\leqq"],
            aside: "< ≧ ≦",
          },
        ],
        [
          { latex: "m" },
          { latex: "n" },
          { class: "separator w5" },
          { label: "1", key: "1" },
          { label: "2", key: "2" },
          { label: "3", key: "3" },
          { latex: "-" },
          { class: "separator w5" },
          { class: "tex", latex: "\\dot{#0}", insert: "$$\\dot{#0}$$" },
          { class: "separator w10" },
          {
            class: "action",
            label: "▲",
            command: ["performWithFeedback", "moveUp"],
          },
          {
            class: "action font-glyph",
            label: "&#x232b;",
            command: ["performWithFeedback", "deleteBackward"],
          },
        ],
        [
          {
            class: "small",
            key: "a",
            label: "a～m",
            variants: ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "\\ell", "m",],
          },
          {
            class: "small",
            key: "n",
            label: "n～z",
            variants: ["n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z",],
          },
          { class: "separator w5" },
          { label: "0", key: "0" },
          { latex: "." },
          { label: "=" },
          { latex: "+" },
          { class: "separator w5" },
          { class: "separator w10" },
          {
            class: "action",
            label: "◀",
            command: ["performWithFeedback", "moveToPreviousChar"],
          },
          {
            class: "action",
            label: "▼",
            command: ["performWithFeedback", "moveDown"],
          },
          {
            class: "action",
            label: "▶",
            command: ["performWithFeedback", "moveToNextChar"],
          },
        ],
      ],
    },
    {
      label: "図形",
      rows: [
        [
          { key: "A", label: "A" },
          { key: "D", label: "D" },
          { class: "separator w5" },
          { label: "7", key: "7" },
          { label: "8", key: "8" },
          { label: "9", key: "9" },
          { latex: "\\div" },
          { class: "separator w5" },
          { class:"tex small", latex: "\\dfrac{#1}{#0}", insert: "$$\\dfrac{#1}{#0}$$" },
          {
            class: "tex ",
            latex: "{#0}^2",
            insert: "$$#@^{2}$$",
          },
          {
            class: "tex ",
            latex: "{#0}^{#0}",
            insert: "$$#@^{#0}$$",
          },
          {
            class: "tex ",
            latex: "{#0}´",
            insert: "$$#@´$$",
          },
        ],
        [
          { key: "B", label: "B" },
          { key: "E", label: "E" },
          { class: "separator w5" },
          { label: "4", key: "4" },
          { label: "5", key: "5" },
          { label: "6", key: "6" },
          { latex: "\\times" },
          { class: "separator w5" },
          { latex: "\\pi" },
          { latex: "\\angle" },
          {
            class: "tex",
            latex: "\\sqrt{#0}",
            insert: "$$\\sqrt{#0}$$",
          },
          {
            class: "tex",
            latex: ">",
            variants: [">", "<", "\\geqq", "\\leqq"],
            aside: "< ≧ ≦",
          },
        ],
        [
          { key: "C", label: "C" },
          { key: "F", label: "F" },
          { class: "separator w5" },
          { label: "1", key: "1" },
          { label: "2", key: "2" },
          { label: "3", key: "3" },
          { latex: "-" },
          { class: "separator w5" },
          { latex: "\\equiv" },
          { latex: "{\\textsf{\\backsim}}", insert: "{\\textsf{\\backsim}}"}, // NFR-3099対応
          {
            class: "action",
            label: "▲",
            command: ["performWithFeedback", "moveUp"],
          },
          {
            class: "action font-glyph",
            label: "&#x232b;",
            command: ["performWithFeedback", "deleteBackward"],
          },
        ],
        [
          {
            class: "small",
            key: "A",
            label: "A～M",
            variants: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M",],
          },
          {
            class: "small",
            key: "N",
            label: "N～Z",
            variants: ["N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z",],
          },
          { class: "separator w5" },
          { label: "0", key: "0" },
          { latex: "." },
          { label: "=" },
          { latex: "+" },
          { class: "separator w5" },
          { latex: "\\triangle" },
          {
            class: "action",
            label: "◀",
            command: ["performWithFeedback", "moveToPreviousChar"],
          },
          {
            class: "action",
            label: "▼",
            command: ["performWithFeedback", "moveDown"],
          },
          {
            class: "action",
            label: "▶",
            command: ["performWithFeedback", "moveToNextChar"],
          },
        ],
      ],
    }
  ])
  const MANAVIRIA_SCIENCE_KEYBOARD_LAYER = ref([
    {
      label: "理科",
      rows: [
        [
          { class: "tex", latex: "{#0}^+", insert: "$$^+$$" },
          { key: "2", label: "2" },
          { key: "3", label: "3" },
          { key: "4", label: "4" },
          { class: "separator w5" },
          { key: "+", label: "+", insert:"\\:+\\:" },
          { key: "→", label: "→" },
          {
            class: "tex",
            latex: "\\left({#0}\\right)",
            insert: "$$(#0)$$",
          },
          { class: "separator w10" },
        ],
        [
          { class: "tex ", latex: "{#0}^-", insert: "$$^-$$" },
          { class: "tex ", latex: "{#0}^2", insert: "$$^2$$" },
          { class: "tex ", latex: "{#0}^3", insert: "$$^3$$" },
          { class: "tex ", latex: "{#0}^4", insert: "$$^4$$" },
          { class: "separator w5" },
          { class: "tex ", latex: "{#0}^´", insert: "$$#@^´$$" },
          { class:"tex small", latex: "\\dfrac{#1}{#0}", insert: "$$\\dfrac{#1}{#0}$$" },
          {
            class: "action",
            label: "▲",
            command: ["performWithFeedback", "moveUp"],
          },
          {
            class: "action font-glyph",
            label: "&#x232b;",
            command: ["performWithFeedback", "deleteBackward"],
          },
        ],
        [
          { class: "tex ", latex: "{#0}_1", insert: "$$_1$$" },
          { class: "tex ", latex: "{#0}_2", insert: "$$_2$$" },
          { class: "tex ", latex: "{#0}_3", insert: "$$_3$$" },
          { class: "tex ", latex: "{#0}_4", insert: "$$_4$$" },
          { class: "separator w5" },
          {
            class: "tex ",
            latex: "{#0}_{#1}",
            insert: "$$#@_{#1}$$",
          },
          {
            class: "action",
            label: "◀",
            command: ["performWithFeedback", "moveToPreviousChar"],
          },
          {
            class: "action",
            label: "▼",
            command: ["performWithFeedback", "moveDown"],
          },
          {
            class: "action",
            label: "▶",
            command: ["performWithFeedback", "moveToNextChar"],
          },
        ],
      ],
    }
  ])
  const inputValue = ref("") // inputfieldに入力された値を保存する変数

  onMounted(() => {
    let mf:any = refMathField.value
    // for active keyboard glyph when hover
    mf.removeAttribute("contenteditable")
    mf.mathVirtualKeyboardPolicy = "manual"
    mf.smartSuperscript = "on"
    if (props.isScience) {
      window.mathVirtualKeyboard.layouts = MANAVIRIA_SCIENCE_KEYBOARD_LAYER.value
      mf.letterShapeStyle = "upright"
      mf.inlineShortcuts = {
        "/": { mode: "text", value: "/" },
      }
    } else {
      window.mathVirtualKeyboard.layouts = MANAVIRIA_MATH_KEYBOARD_LAYER.value
      mf.letterShapeStyle = "tex"
      mf.inlineShortcuts = [] // NFR-4640:shortcutキーは必要に応じて個別に設定するようにする。
    }
    mf.addEventListener('focusout', () => window.mathVirtualKeyboard.hide());
    let mutationObserver = new MutationObserver(() => {
      if(document.activeElement === refMathField.value){
        if (window.mathVirtualKeyboard.visible) {
          setKeyboardPosition();
        }
      }
    });
    mutationObserver.observe(document.body, {
      childList: true,
    });
    // remove keyboard hover tooltip
    delete mf.shadowRoot.querySelector(".ML__virtual-keyboard-toggle").dataset.ml__tooltip
    mounted(mf)
  })

  function mounted(mf: any) {
    let agent = window.navigator.userAgent.toLowerCase();
    let mf_parent = mf.parentNode.parentNode
    let parent_div: any = mf.shadowRoot.querySelector("span")
    parent_div.style.position = "relative" // 親要素ポジションをrelativeで子要素をabsoluteにして追加要素を重ねる
    // placeholderテキストを表示する要素を追加
    let placeHolderElem = document.createElement("div")
    placeHolderElem.className = "placeholder_text"
    placeHolderElem.style.position = "absolute"
    placeHolderElem.style.display = "inline-block"
    placeHolderElem.style.marginTop = mf_parent.classList.contains("stringBox") ? "1px" : "6px"
    placeHolderElem.style.color = "#c8c8c8"
    placeHolderElem.style.whiteSpace = "nowrap"
    if(props.placeHolder){
      placeHolderElem.innerHTML = md.render(props.placeHolder)
      let placeHolderChildElem:any = placeHolderElem.childNodes[0]
      placeHolderChildElem.style.margin = 0;
    }
    parent_div.insertAdjacentElement("afterbegin", placeHolderElem)

    // 解答欄の幅を固定で設定したのでその中のmath fieldも併せて設定（分数の場合math fieldが大きくなることを防ぐため）
    let field_container:any = mf.shadowRoot.querySelector(".ML__container")
    field_container.style.height = "43px"
    let ml_field:any = mf.shadowRoot.querySelector(".ML__content")
    ml_field.style.height = "inherit"
    // NFR-3047対応：ipadだけキーボードの矢印キーでキャレットが動かない
    if(agent.indexOf('ipad') > -1 || agent.indexOf('macintosh') > -1 && 'ontouchend' in document){
      ml_field.style.overflowX = "auto"
    }
    // set suffix text
    if(props.suffix){
      let suffix_element = document.createElement("span")
      suffix_element.style.alignSelf = "center";
      suffix_element.style.marginLeft = "6px";
      suffix_element.style.whiteSpace = "nowrap";
      suffix_element.innerHTML = md.render(props.suffix)
      // change color if focus
      mf.addEventListener("focus", function() {
        suffix_element.style.color = "#2562f7";
      }, false);
      // change color to initial if blur
      mf.addEventListener("blur", function() {
        suffix_element.style.color = "initial";
      }, false)
      ml_field.parentNode.insertBefore(suffix_element, ml_field.nextSibling)
    }
    // fetch toggle button from DOM
    const toggleBtn: HTMLElement =  mf.shadowRoot.querySelector(".ML__virtual-keyboard-toggle")
    // 日本語入力は未確定の場合e.target.value.lengthが0なのでそれだけでplaceholderテキスト表示非表示区分ができないため
    // compositionイベント使用
    mf.addEventListener("compositionstart", ()=>{
      workStore.saveCompositionInProgress(true)
      // if keyboard is visiable then hide keyboard for composition start
      if(window.mathVirtualKeyboard.visible){
        window.mathVirtualKeyboard.hide()
      }
      // disabled toggle button if composition start
      toggleBtn.style.pointerEvents = "none";
      toggleBtn.style.opacity = "0.4";
    })
    mf.addEventListener("compositionend", (e) => {
      if (inputValue.value.length == 0) {
        if (props.placeHolder) {
          placeHolderElem.innerHTML = md.render(props.placeHolder)
          let placeHolderChildElem: any = placeHolderElem.childNodes[0]
          placeHolderChildElem.style.margin = 0;
        }
      }
      // 日本語で「そうじ」を入力して出る相似記号をsans-serifフォントに変更する（ipad対応のため）NFR-3011
      // NFR-4619 英字 (A-Z, a-z) および日本語のひらがな、カタカナ、漢字、半角カタカナ + \backsimの組み合わせの場合置換
      const regex = /([A-Za-z\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\uFF66-\uFF9F])\\backsim/gu;
      e.target.value = e.target.value.replace(regex, '$1{\\textsf{\\backsim}}');
      e.target.value = e.target.value.replace(/(∽)/g, "{\\textsf{\\backsim}}")
      // enabled toggle button after composition end
      toggleBtn.style.pointerEvents = "initial";
      toggleBtn.style.opacity = "initial";
      workStore.saveCompositionInProgress(false)
    })
    window.visualViewport.addEventListener("resize", resizeHandler)
    // save answerText to restoreText because of props cannot be immutable
    restoreText.value = props.prevAnswerText
    if(restoreText.value){
      // set placeholder to empty to avoid overlap when press skip prev button
      if(props.placeHolder){
        placeHolderElem.innerHTML = ""
      }
      // make correct latex format
      restoreText.value = restoreText.value.replace(/[$]/g, "")
      restoreText.value = restoreText.value.replace(/(⬚)/g , '\\placeholder{⬚}')
      restoreText.value = restoreText.value.replace(/(\\dfrac)/g , '\\frac')
      restoreText.value = restoreText.value.replace(/(∽)/g, "{\\textsf{\\backsim}}")
      // save restoreText to input value
      mf.value = restoreText.value
    }
  }
  // orientationchangeイベントではsafariでは高さ更新ができないのでresizeイベント使用
  function resizeHandler(){
    if(document.activeElement === refMathField.value){
      if (window.mathVirtualKeyboard.visible) {
        setKeyboardPosition();
      }
    }
  }
  function setInputValue(e){
    let mf:any = refMathField.value;
    let placeholder:any = mf.shadowRoot.querySelector(".placeholder_text")
    if(restoreText.value.length !== 0){
      restoreText.value = ""
    }

    if(e.target.value.length > 0){ // inputefieldで何か入力されたらplaceholderは表示しない。
      placeholder.innerHTML = ""
    }else{
      if(workStore.compositionInProgress){ // 日本語入力中の場合もplaceholderは表示しない
        placeholder.innerHTML = ""
      }else{
        if (props.placeHolder) {
          placeholder.innerHTML = md.render(props.placeHolder)
          let placeHolderChildElem: any = placeholder.childNodes[0]
          placeHolderChildElem.style.margin = 0;
        }
      }
    }
    inputValue.value = e.target.value
    emit('set-latex', e.target.value.replace(/(\\placeholder{})/g, '▢'))
  }
  function setKeyboardPosition(){
    let documentStyle = document.documentElement.style;
    // NFR-4079 get the document style variable '--scroll-y' to adjust keyboard position
    let scroll_y = documentStyle.getPropertyValue("--scroll-y")
    let scroll_height = 0;
    if(scroll_y.length){
      scroll_height = parseInt(scroll_y.replace("px", ""))
    }
    let mf:any = refMathField.value;
    let keyboard:any = document.querySelector('.MLK__backdrop');
    let mlk_rows:any = document.querySelectorAll('.MLK__rows')
    let mlk_plate_height: number = document.querySelector(".MLK__plate").getBoundingClientRect().height + 20
    // NFR-4079 mathlive0.95.5で使用するcqw単位はios15以下では使わないのでバージョンをチェックして%に変更する。
    const ios_version = ipadOsVersion()
    if(ios_version <= 15){
      mlk_rows.forEach(function (element) {
        element.style.setProperty('--_keycap-width', 'min(var(--_keycap-max-width), 10%)');
      });
    }
    // 仮想キーボードのtop位置で前取得したscrolledHeightを足す
    keyboard.style.top = mf.getBoundingClientRect().top - mlk_plate_height + scroll_height + 'px';
    let mf_parent:any = mf.parentNode.parentNode // inputfield <li> の部分
    let left:number = mf_parent.getBoundingClientRect().x
    let keyboard_width: number = keyboard.getBoundingClientRect().width
    // 対象のelementのleftとキーボード幅の合計値が画面を超える時はキーボード表示はelementの右端設定
    if(left + keyboard_width > document.body.clientWidth){
      keyboard.style.left = (mf_parent.getBoundingClientRect().right - keyboard_width) + 'px';
    }else{
      keyboard.style.left = left + 'px';
    }
  }
  // ipadOSのipadだけ判定（生徒の対象のOSはchrome, window(edge), ipadのsafari)
  function ipadOsVersion(){
    let agent = window.navigator.userAgent.toLowerCase();
    if(agent.indexOf('ipad') > -1 || agent.indexOf('macintosh') > -1 && 'ontouchend' in document){
      let version = agent.match(/version\/(\d+).(\d+)/)
      if(version){
        return parseInt(version[1])
      }
    }
  }
  onUnmounted(() => {
    // NFR-4668 このコンポネントが破棄されるときキーボードがvisibleになった場合hideするようにする。
    if(window.mathVirtualKeyboard.visible){
      window.mathVirtualKeyboard.hide()
    }
  })
</script>